import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Paper,
  Card,
  Box,
  Fab,
  Snackbar,
  Alert,
  InputLabel,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { red } from "@mui/material/colors";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import * as Icons from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { headers, oauthUserMgmt, tenant, userLogOut } from "../../utils/utils";
import "react-datepicker/dist/react-datepicker.css";
import Header from "./Header";
import TicketCard from "./TicketCard";
import NewTicket from "./NewTicket";
import InviteRequest from "./InviteRequest";
import BoardSelection from "./BoardSelection";
import ChatBot from "./ChatBot";
import NotificationComponent from "../../utils/NotificationComponent";

const Ticket = () => {
  const [formData, setFormData] = useState({
    subject: " ",
    message: "",
    priority: "",
    memberIds: [],
    tags: [],
    id: null,
    board: localStorage.getItem("board"),
    dueDate: null,
    startDateTime: null,
    endDateTime: null,
    dynamicFields: {},
    targetDate: null,
    status: "",
    ticketItems: [],
  });
  const [tagsList, setTagsList] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isBoardAllow, setIsBoardAllow] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [ticketDataLoad, setTicketDataLoad] = useState(false);
  const [ticketStatus, setTicketStatus] = useState([]);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);
  const [reportLoaded, setReportLoaded] = useState({
    id: "",
    frequency: "daily",
    story: "",
    email: "",
    reportName: "",
    tenant: "",
    time: "",
    day: [],
    date: "",
  });

  const fetchLabel = async () => {
    if (tagsList.length === 0) {
      try {
        const response = await fetch(
          process.env.REACT_APP_SYS_CONFIG_MGMT +
          "/systemConfig/config?name=TicketLabel-" +
          localStorage.getItem("board"),
          {
            method: "GET",
            headers: headers(),
          }
        );

        const result = await response.json();
        setTagsList(result);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    }
  };

  const updateTicketStatus = (ticketObject) => {
    setTicketStatus((prevTicketStatus) => {
      return prevTicketStatus.map((status) => {
        if (status.label === ticketObject.status) {
          // Create a new array without mutating the existing one
          const updatedTicketList = status.ticketList.filter(
            (ticket) => ticket.id !== ticketObject.id
          );

          // Add the updated ticket
          return { ...status, ticketList: [...updatedTicketList, ticketObject] };
        }
        return status; // Keep other statuses unchanged
      });
    });
  };


  const fetchAllMember = async () => {
    if (memberList.length === 0)
      try {
        const response = await fetch(
          process.env.REACT_APP_USER_MGMT +
          "/admin/realms/eVyoog/users?q=tenant:" +
          tenant(),
          {
            method: "GET",
            headers: oauthUserMgmt(),
          }
        );
        if (response.ok) {
          const result = await response.json();

          const usersWithFullNameAndId = result
            ? result
              .filter((item) => item.firstName)
              .map((user) => ({
                id: user.id,
                fullName: `${user.firstName ? user.firstName : ""} ${user.lastName}`,
              }))
            : null;

          setMemberList(usersWithFullNameAndId);
        }
      } catch (error) {
        userLogOut();
      }
  };

  // fetchAllMember only pulls users tagged with this TMS tenant, so a member assigned from
  // PMS (whose Keycloak "tenant" attribute is a PMS tenant, not this one) never shows up in
  // memberList — the assigned chip in MemberSelector then silently fails to render since it
  // can't find a name for that id. Resolve those specific ids individually instead of widening
  // the tenant-scoped list (which drives the "add member" search and should stay scoped to
  // this tenant's own users).
  const resolveMissingMembers = async (ids) => {
    const missing = (ids || []).filter((id) => !memberList.some((m) => m.id === id));
    if (missing.length === 0) return;

    const resolved = await Promise.all(
      missing.map(async (id) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${id}`,
            { method: "GET", headers: oauthUserMgmt() }
          );
          if (!response.ok) return null; // e.g. a PMS placeholder owner who's never logged in yet
          const user = await response.json();
          if (!user.firstName) return null;
          return { id: user.id, fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim() };
        } catch {
          return null;
        }
      })
    );

    const found = resolved.filter(Boolean);
    if (found.length > 0) {
      setMemberList((prev) => [
        ...prev,
        ...found.filter((f) => !prev.some((m) => m.id === f.id)),
      ]);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("board")) {
      setIsPopupOpen(true);
    }
    fetchLabel();
    fetchAllMember();
  }, []);

  useEffect(() => { }, [formData]);


  const updateTicketList = useCallback(
    (newTicketList, label) => {
      if (!newTicketList || !label) {
        return;
      }

      setTicketStatus((prevTicketStatus) =>
        prevTicketStatus.map((status) =>
          status.label === label
            ? {
              ...status,
              ticketList: [...status.ticketList, ...newTicketList],
            }
            : status
        )
      );
    },
    [setTicketStatus]
  );

  const cardRefs = useRef([]);

  const ticketDataLoading = useCallback(
    async (label, pageNumber) => {
      const inputValue = searchValue;

      // Construct URL based on searchText availability
      let url = `${process.env.REACT_APP_TICKET_SERVER}/ticket/list?pageNumber=${pageNumber}&pageSize=10`;

      // Conditionally use a different URL if searchValue exists
      if (inputValue && inputValue.trim() !== "") {
        url = `${process.env.REACT_APP_TICKET_SERVER}/ticket/list?pageNumber=0&pageSize=100`;
      }

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: headers(), // If headers() changes, consider memoizing it with useMemo
          body: JSON.stringify({
            board: localStorage.getItem("board"),
            status: label || "",
            searchText: inputValue || "",
          }),
        });

        // Update the ticket status: increment pageNumber for the matching label
        setTicketStatus((prevTicketStatus) =>
          prevTicketStatus.map((status) =>
            status.label === label
              ? { ...status, pageNumber: pageNumber + 1 }
              : status
          )
        );

        const result = await response.json();

        if (inputValue && inputValue.trim() !== "") {
          setTicketStatus((prevTicketStatus) =>
            prevTicketStatus.map((status) =>
              status.label === label
                ? { ...status, ticketList: result.content }
                : status
            )
          );
        } else {
          updateTicketList(result.content, label);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      }
    },
    [searchValue, setTicketStatus, updateTicketList]
  );

  const loadTickets = useCallback(() => {
    if (ticketStatus) {
      ticketStatus.forEach((ticket) => {
        ticketDataLoading(ticket.label, ticket.pageNumber);
      });
    }
  }, [ticketStatus, ticketDataLoading]);

  useEffect(() => {
    if (ticketDataLoad === false) {
      if (ticketStatus && ticketStatus.length > 0 && !ticketsLoaded) {
        loadTickets();
        setTicketsLoaded(true);
        setTicketDataLoad(true);
      }
    }
  }, [ticketStatus, ticketDataLoad, ticketsLoaded, loadTickets]);

  const theme = createTheme({
    typography: {
      fontFamily:
        '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"', // Change this to your desired font,
      fontWeight: 600,
      lineHeight: 1.57143,
    },
  });

  const LoadIcon = ({ iconName = "Assignment" }) => {
    const defaultIcon = "Task"; // Set your default icon here
    const IconComponent = Icons[iconName] || Icons[defaultIcon];

    return <IconComponent />;
  };


  const displayAlertMessage = (message) => {
    setAlertMessage(message);
    setSnackbarOpen(true);

    setTimeout(() => {
      setSnackbarOpen(false);
    }, 3000);
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [openNewTicket, setOpenNewTicket] = useState(false);
  const handleNewTicket = () => {
    setOpenNewTicket((prevState) => !prevState);
    setAttachments([]);
  };
  const handleSettingsClick = () => setOpenSettingsDialog(true);
  const handleScroll = (index) => (event) => {
    const { scrollTop, scrollHeight, clientHeight } = cardRefs.current[index];
    if (scrollHeight - scrollTop === clientHeight) {
      ticketDataLoading(
        ticketStatus[index].label,
        ticketStatus[index].pageNumber
      );
    }
  };
  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const { source, destination, draggableId } = result;

    const currentTicket = ticketStatus
      .flatMap((status) => status.ticketList) // Flatten the ticket lists to get all tickets
      .find((ticket) => ticket.id.toString() === draggableId.toString()); // Adjust based on how you identify tickets
    currentTicket.status = destination.droppableId;

    fetch(
      process.env.REACT_APP_TICKET_SERVER +
      "/ticket/update/" +
      currentTicket.id,
      {
        method: "PUT",
        body: JSON.stringify(currentTicket),
        headers: headers(),
      }
    );
    if (source.droppableId === destination.droppableId) {
      const updatedTicketStatus = ticketStatus.map((status) => {
        if (status.label === source.droppableId) {
          const newTicketList = Array.from(status.ticketList);
          const [movedTicket] = newTicketList.splice(source.index, 1);
          newTicketList.splice(destination.index, 0, movedTicket);
          return { ...status, ticketList: newTicketList };
        }
        return status;
      });

      setTicketStatus(updatedTicketStatus);
    } else {
      // Moving items from one list to another
      const sourceStatus = ticketStatus.find(
        (status) => status.label === source.droppableId
      );
      const destinationStatus = ticketStatus.find(
        (status) => status.label === destination.droppableId
      );

      const newSourceTicketList = Array.from(sourceStatus.ticketList);
      const [movedTicket] = newSourceTicketList.splice(source.index, 1);
      const newDestinationTicketList = Array.from(destinationStatus.ticketList);
      newDestinationTicketList.splice(destination.index, 0, movedTicket);

      const updatedTicketStatus = ticketStatus.map((status) => {
        if (status.label === source.droppableId) {
          return { ...status, ticketList: newSourceTicketList };
        } else if (status.label === destination.droppableId) {
          return { ...status, ticketList: newDestinationTicketList };
        }
        return status;
      });

      setTicketStatus(updatedTicketStatus);
    }
  };
  useEffect(() => {
    loadTickets();
  }, [searchValue]);

  const handleSearch = (query) => {
    setSearchValue(query);
  };

  const openTicket = async (id) => {
    const response = await fetch(
      process.env.REACT_APP_TICKET_SERVER + "/ticket/show/" + id,
      {
        method: "GET",
        headers: headers(),
      }
    );
    const data = await response.json(); // Parse the JSON response
    // Set formData with the response data
    setFormData((prevData) => ({
      ...prevData,
      id: data.id,
      code: data.code,
      subject: data.subject, // Assuming you want to set these fields
      message: data.message,
      priority: data.priority,
      memberIds: data.memberIds || [], // Default to an empty array if memberIds is not provided
      memberNames: data.memberNames || {},
      dueDate: data.dueDate,
      startDateTime: data.startDateTime,
      endDateTime: data.endDateTime,
      dynamicFields: data.dynamicFields || {},
      targetDate: data.targetDate,
      status: data.status,
      totalWorkSeconds: data.totalWorkSeconds,
      tags: data.tags || [],
      ticketItems: data.ticketItems || [],
    }));
    resolveMissingMembers(data.memberIds);
    loadAttachments(data.id);
    handleNewTicket();
  };

  const loadAttachments = async (resourceId) => {
    if (resourceId) {
      try {
        const response = await fetch(
          process.env.REACT_APP_ATTACHMENT +
          `/attachment/filenames/${resourceId}`,
          {
            method: "GET",
            headers: headers(),
          }
        );
        const data = await response.json();
        if (data) {
          setAttachments(data);
        }
      } catch (error) {
        console.error("Error fetching filenames:", error);
      }
    }
  };

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const createTicket = (statusLabel) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      subject: "",
      code: "",
      message: "",
      priority: "",
      memberIds: [],
      tags: [],
      id: null,
      dueDate: null,
      startDateTime: null,
      endDateTime: null,
      dynamicFields: {},
      targetDate: null,
      status: statusLabel,
      ticketItems: [],
    }));
    handleNewTicket();
  };

  return (
    <div style={{ backgroundColor: "#f0f8ff" }}>

      <NotificationComponent board={localStorage.getItem("board")} updateTicketStatus={updateTicketStatus} setTicketStatus={setTicketStatus} />

      <ThemeProvider theme={theme}>
        <Card>
          {ticketStatus && <Header
            isBoardAllow={isBoardAllow}
            setIsBoardAllow={setIsBoardAllow}
            setTicketStatus={setTicketStatus}
            fetchLabel={fetchLabel}
            fetchAllMemberFromTicket={fetchAllMember}
            memberList={memberList}
            tagsList={tagsList}
            setTagsList={setTagsList}
            ticketStatus={ticketStatus}
            setOpenSettingsDialog={setOpenSettingsDialog}
            openSettingsDialog={openSettingsDialog}
            handleSettingsClick={handleSettingsClick}
            LoadIcon={LoadIcon}
            onSearch={handleSearch}
            displayAlertMessage={displayAlertMessage}
            reportLoaded={reportLoaded}
            setReportLoaded={setReportLoaded}
          />}
        </Card>
      </ThemeProvider>
      {/* Popup to select a board */}
      {isPopupOpen && (
        <BoardSelection
          open={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
        />
      )}

      {isBoardAllow ? (
        <>
          <Fab
            variant="extended"
            onClick={() => createTicket(ticketStatus?.[0]?.label || "")}
            size="medium"
            color="error"
            sx={{
              bgcolor: red[500],
              color: "white",
              fontSize: "10px",
              fontFamily: "Arial, sans-serif",
              position: "fixed", // Makes the button fixed on the screen
              bottom: "4%", // Distance from the bottom of the screen
              right: 16, // Distance from the right side of the screen
              zIndex: 1000, // Ensures it appears above other elements
            }}
          >
            <AddCircleIcon sx={{ mr: 1 }} />
            New Card
          </Fab>

          <div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Box
                sx={{
                  display: "flex", // Flexbox layout
                  overflowX: "auto", // Enable horizontal scrolling
                  gap: 2, // Space between items
                  padding: 2,
                }}
              >
                {ticketStatus.map((itm, index) => (
                  <Paper
                    sx={{
                      backgroundColor: "transparent", // Make background transparent
                      boxShadow: "none", // Remove the default shadow if needed
                      minWidth: 400,
                    }}
                    key={index}
                  >
                    <Droppable key={itm.label} droppableId={itm.label}>
                      {(provided) => (
                        <div>
                          <center>
                            <Fab
                              variant="extended"
                              size="medium"
                              sx={{
                                bgcolor: itm.color,
                                color: "white",
                                fontSize: "10px",
                              }}
                            >
                              <LoadIcon iconName={itm.icon} fontSize="12px" />
                              {itm.label}
                            </Fab>
                          </center>
                          <br />
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            <Card
                              ref={(el) => (cardRefs.current[index] = el)}
                              onScroll={handleScroll(index)}
                              sx={{
                                width: "380px",
                                minHeight: "70vh",
                                maxHeight: "70vh",
                                paddingTop: "4px",
                                background: " #F1F2F4  ",
                                padding: "10px",
                                overflow: "auto",
                              }}
                            >
                              {itm.ticketList && itm.ticketList
                                .sort((a, b) => b.id - a.id)
                                .map((item, index) => (
                                  <Draggable
                                    key={item?.id || "00"}
                                    draggableId={item?.id?.toString() || "00"}
                                    index={index}
                                  >
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <TicketCard
                                          position={index}
                                          ticketStatus={ticketStatus}
                                          setTicketStatus={setTicketStatus}
                                          data={item}
                                          tagsList={tagsList}
                                          memberList={memberList}
                                          color={itm.color}
                                          openTicket={openTicket}
                                          icon={
                                            <LoadIcon iconName={itm.icon} />
                                          }
                                          updateTicketStatus={updateTicketStatus}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </Card>

                            <div
                              key={itm.label}
                              onClick={() => createTicket(itm.label)}
                              style={{
                                position: "relative",
                              }}
                            >
                              <InputLabel
                                key={itm.label}
                                sx={{
                                  cursor: "pointer",
                                  display: "flex",
                                  fontSize: "15px",
                                  alignItems: "center",
                                  lineHeight: "40px",
                                  margin: 0,
                                  paddingTop: "15px",
                                  color: "lightgray",
                                  fontFamily: "Arial, sans-serif",
                                  position: "relative",
                                  bottom: 50,
                                  right: 0,
                                  justifyContent: "center",
                                  transition: "color 0.3s ease", // Smooth transition
                                  "&:hover": {
                                    color: "black", // Highlight color on hover
                                  },
                                }}
                              >
                                New card +

                              </InputLabel>

                            </div>
                          </div>
                        </div>
                      )}
                    </Droppable>
                  </Paper>
                ))}
              </Box>
            </DragDropContext>
          </div>
        </>
      ) : (
        <>{isBoardAllow === false ? <InviteRequest /> : ""}</>
      )}

      <NewTicket
        openSettingsDialog={openSettingsDialog}
        openNewTicket={openNewTicket}
        setOpenNewTicket={setOpenNewTicket}
        ticketStatus={ticketStatus}
        setTicketStatus={setTicketStatus}
        handleNewTicket={handleNewTicket}
        firstTicketLabelName={ticketStatus?.[0]?.label || ""}
        formData={formData}
        setFormData={setFormData}
        tagList={tagsList}
        memberList={memberList}
        loadAttachments={loadAttachments}
        attachments={attachments}
        displayAlertMessage={displayAlertMessage}
        updateTicketStatus={updateTicketStatus}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#1B5E20", // Dark green background
            color: "#FFFFFF", // White text
            fontWeight: "bold", // Bold text
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", // Add a subtle shadow
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
<ChatBot/>
    </div>
  );
};

export default Ticket;
